/**
 * ApplicationForm
 *
 * Branded job application form matching Contact/Demo form styling.
 * Submits as multipart/form-data to /api/apply (Cloudflare Worker),
 * which creates a Monday.com item and attaches the resume file.
 */

import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, CheckCircle, ChevronDown, UploadCloud, X, FileText } from 'lucide-react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ROLE_OPTIONS = [
  'Business Development Manager',
  'Client Success Manager',
  'Creative Technologist',
  'Data / AI Engineer',
  'Finance Manager',
  'Full-Stack Engineer',
  'Implementation / Onboarding Specialist',
  'Operations Manager',
  'Partnerships / Channel Manager',
  'Product Manager',
  'Solutions Architect',
  'Other',
] as const;

const COUNTRY_CODES: { code: string; iso: string; label: string }[] = [
  { code: '+1',   iso: 'US', label: '🇺🇸 United States (+1)' },
  { code: '+1',   iso: 'CA', label: '🇨🇦 Canada (+1)' },
  { code: '+44',  iso: 'GB', label: '🇬🇧 United Kingdom (+44)' },
  { code: '+61',  iso: 'AU', label: '🇦🇺 Australia (+61)' },
  { code: '+64',  iso: 'NZ', label: '🇳🇿 New Zealand (+64)' },
  { code: '+353', iso: 'IE', label: '🇮🇪 Ireland (+353)' },
  { code: '+49',  iso: 'DE', label: '🇩🇪 Germany (+49)' },
  { code: '+33',  iso: 'FR', label: '🇫🇷 France (+33)' },
  { code: '+34',  iso: 'ES', label: '🇪🇸 Spain (+34)' },
  { code: '+39',  iso: 'IT', label: '🇮🇹 Italy (+39)' },
  { code: '+31',  iso: 'NL', label: '🇳🇱 Netherlands (+31)' },
  { code: '+32',  iso: 'BE', label: '🇧🇪 Belgium (+32)' },
  { code: '+41',  iso: 'CH', label: '🇨🇭 Switzerland (+41)' },
  { code: '+43',  iso: 'AT', label: '🇦🇹 Austria (+43)' },
  { code: '+46',  iso: 'SE', label: '🇸🇪 Sweden (+46)' },
  { code: '+47',  iso: 'NO', label: '🇳🇴 Norway (+47)' },
  { code: '+45',  iso: 'DK', label: '🇩🇰 Denmark (+45)' },
  { code: '+358', iso: 'FI', label: '🇫🇮 Finland (+358)' },
  { code: '+52',  iso: 'MX', label: '🇲🇽 Mexico (+52)' },
  { code: '+55',  iso: 'BR', label: '🇧🇷 Brazil (+55)' },
  { code: '+54',  iso: 'AR', label: '🇦🇷 Argentina (+54)' },
  { code: '+57',  iso: 'CO', label: '🇨🇴 Colombia (+57)' },
  { code: '+91',  iso: 'IN', label: '🇮🇳 India (+91)' },
  { code: '+81',  iso: 'JP', label: '🇯🇵 Japan (+81)' },
  { code: '+82',  iso: 'KR', label: '🇰🇷 South Korea (+82)' },
  { code: '+86',  iso: 'CN', label: '🇨🇳 China (+86)' },
  { code: '+65',  iso: 'SG', label: '🇸🇬 Singapore (+65)' },
  { code: '+971', iso: 'AE', label: '🇦🇪 United Arab Emirates (+971)' },
  { code: '+27',  iso: 'ZA', label: '🇿🇦 South Africa (+27)' },
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_TYPES = ['.pdf', '.doc', '.docx'];
const ACCEPTED_MIME  = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function inputClass(hasError: boolean) {
  return `w-full px-4 py-3 rounded-lg border ${
    hasError ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'
  } bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#635bff] focus:border-transparent transition-colors`;
}

function formatFileSize(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(0)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface FormState {
  name: string;
  email: string;
  countryCode: string;
  countryIso: string;
  phone: string;
  role: string;
  alignment: string;
}

const EMPTY: FormState = {
  name: '',
  email: '',
  countryCode: '+1',
  countryIso: 'US',
  phone: '',
  role: '',
  alignment: '',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ApplicationForm: React.FC = () => {
  const [form, setForm]           = useState<FormState>(EMPTY);
  const [resume, setResume]       = useState<File | null>(null);
  const [dragOver, setDragOver]   = useState(false);
  const [errors, setErrors]       = useState<Partial<Record<keyof FormState | 'resume', string>>>({});
  const [agreed, setAgreed]       = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // ---- field change --------------------------------------------------------

  const set = (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setForm(prev => ({ ...prev, [field]: e.target.value }));
      if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
    };

  const setCountry = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = COUNTRY_CODES.find(c => c.label === e.target.value);
    if (selected) setForm(prev => ({ ...prev, countryCode: selected.code, countryIso: selected.iso }));
  };

  // ---- file handling -------------------------------------------------------

  const acceptFile = useCallback((file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const validByMime = ACCEPTED_MIME.includes(file.type);
    const validByExt  = ['pdf', 'doc', 'docx'].includes(ext);
    if (!validByMime && !validByExt) {
      setErrors(prev => ({ ...prev, resume: 'Please upload a PDF or Word document (.pdf, .doc, .docx)' }));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setErrors(prev => ({ ...prev, resume: `File must be under ${formatFileSize(MAX_FILE_SIZE)}` }));
      return;
    }
    setResume(file);
    setErrors(prev => ({ ...prev, resume: undefined }));
  }, []);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) acceptFile(e.target.files[0]);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.[0]) acceptFile(e.dataTransfer.files[0]);
  };

  // ---- validation ----------------------------------------------------------

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState | 'resume', string>> = {};

    if (!form.name.trim()) next.name = 'Full name is required';
    else if (form.name.trim().length < 2) next.name = 'Name must be at least 2 characters';

    if (!form.email.trim()) next.email = 'Email address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = 'Enter a valid email address';

    if (!form.phone.trim()) next.phone = 'Phone number is required';
    else if (form.phone.replace(/\D/g, '').length < 7) next.phone = 'Enter a valid phone number';

    if (!form.role) next.role = 'Please select the role that best fits you';

    if (!resume) next.resume = 'Please attach your resume';

    if (!form.alignment.trim()) next.alignment = 'Please tell us why you feel aligned to Adventive';
    else if (form.alignment.trim().length < 20) next.alignment = 'Please provide at least a few sentences';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // Is the form ready to submit (drives disabled state)?
  const isReady =
    form.name.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) &&
    form.phone.replace(/\D/g, '').length >= 7 &&
    !!form.role &&
    !!resume &&
    form.alignment.trim().length >= 20 &&
    agreed;

  // ---- submit --------------------------------------------------------------

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !agreed) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const fd = new FormData();
      fd.append('name',        form.name.trim());
      fd.append('email',       form.email.trim().toLowerCase());
      fd.append('countryCode', form.countryCode);
      fd.append('countryIso',  form.countryIso);
      fd.append('phone',       form.phone.trim());
      fd.append('role',        form.role);
      fd.append('alignment',   form.alignment.trim());
      if (resume) fd.append('resume', resume, resume.name);

      const res = await fetch('/api/apply', { method: 'POST', body: fd });
      const data = await res.json() as { success?: boolean; error?: string; details?: unknown };

      if (!res.ok || !data.success) {
        const detail = data.details ? ` — ${JSON.stringify(data.details)}` : '';
        throw new Error((data.error || `Submission failed (${res.status})`) + detail);
      }

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- success state -------------------------------------------------------

  if (submitted) {
    return (
      <div className="text-center py-16 px-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 mb-6">
          <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">Application Received</h2>
        <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto">
          Thank you for applying to Adventive. We'll review your submission and be in touch soon.
        </p>
      </div>
    );
  }

  // ---- form ----------------------------------------------------------------

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6">

      {/* Full Name */}
      <div>
        <label htmlFor="apply-name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Full Name <span aria-hidden="true">*</span>
        </label>
        <input
          id="apply-name"
          type="text"
          placeholder="Jane Smith"
          value={form.name}
          onChange={set('name')}
          autoComplete="name"
          aria-required="true"
          aria-describedby={errors.name ? 'apply-name-error' : undefined}
          aria-invalid={!!errors.name}
          className={inputClass(!!errors.name)}
        />
        {errors.name && <p id="apply-name-error" className="mt-1 text-sm text-red-500" role="alert">{errors.name}</p>}
      </div>

      {/* Email */}
      <div>
        <label htmlFor="apply-email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Email Address <span aria-hidden="true">*</span>
        </label>
        <input
          id="apply-email"
          type="email"
          placeholder="jane@example.com"
          value={form.email}
          onChange={set('email')}
          autoComplete="email"
          aria-required="true"
          aria-describedby={errors.email ? 'apply-email-error' : undefined}
          aria-invalid={!!errors.email}
          className={inputClass(!!errors.email)}
        />
        {errors.email && <p id="apply-email-error" className="mt-1 text-sm text-red-500" role="alert">{errors.email}</p>}
      </div>

      {/* Phone — country code + number */}
      <div>
        <label htmlFor="apply-phone" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Phone Number <span aria-hidden="true">*</span>
        </label>
        <div className="flex gap-2">
          {/* Country code */}
          <div className="relative flex-shrink-0">
            <label htmlFor="apply-country" className="sr-only">Country code</label>
            <select
              id="apply-country"
              value={COUNTRY_CODES.find(c => c.code === form.countryCode && c.iso === form.countryIso)?.label ?? COUNTRY_CODES[0].label}
              onChange={setCountry}
              aria-label="Country code"
              className={`h-full pl-3 pr-8 py-3 rounded-lg border ${
                errors.phone ? 'border-red-500' : 'border-slate-200 dark:border-slate-600'
              } bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#635bff] focus:border-transparent appearance-none transition-colors`}
            >
              {COUNTRY_CODES.map(c => (
                <option key={c.iso} value={c.label}>{c.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" aria-hidden="true" />
          </div>
          {/* Number */}
          <input
            id="apply-phone"
            type="tel"
            placeholder="(555) 000-0000"
            value={form.phone}
            onChange={set('phone')}
            autoComplete="tel-national"
            aria-required="true"
            aria-describedby={errors.phone ? 'apply-phone-error' : undefined}
            aria-invalid={!!errors.phone}
            className={`flex-1 ${inputClass(!!errors.phone)}`}
          />
        </div>
        {errors.phone && <p id="apply-phone-error" className="mt-1 text-sm text-red-500" role="alert">{errors.phone}</p>}
      </div>

      {/* Role Best Fit */}
      <div>
        <label htmlFor="apply-role" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Role Best Fit <span aria-hidden="true">*</span>
        </label>
        <div className="relative">
          <select
            id="apply-role"
            value={form.role}
            onChange={set('role')}
            aria-required="true"
            aria-describedby={errors.role ? 'apply-role-error' : undefined}
            aria-invalid={!!errors.role}
            className={`${inputClass(!!errors.role)} appearance-none pr-10`}
          >
            <option value="">Select a role…</option>
            {ROLE_OPTIONS.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" aria-hidden="true" />
        </div>
        {errors.role && <p id="apply-role-error" className="mt-1 text-sm text-red-500" role="alert">{errors.role}</p>}
      </div>

      {/* Resume Upload — dropzone */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Resume *
        </label>

        {resume ? (
          /* File selected — show name + remove */
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-[#635bff]/40 bg-[#635bff]/5 dark:bg-[#635bff]/10">
            <FileText className="w-5 h-5 text-[#635bff] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{resume.name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{formatFileSize(resume.size)}</p>
            </div>
            <button
              type="button"
              onClick={() => { setResume(null); if (fileRef.current) fileRef.current.value = ''; }}
              className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors flex-shrink-0"
              aria-label="Remove file"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          /* Dropzone */
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            className={`relative flex flex-col items-center justify-center gap-3 px-6 py-10 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
              dragOver
                ? 'border-[#635bff] bg-[#635bff]/5 dark:bg-[#635bff]/10'
                : errors.resume
                ? 'border-red-400 dark:border-red-500 hover:border-red-300'
                : 'border-slate-200 dark:border-white/10 hover:border-[#635bff]/50 dark:hover:border-[#635bff]/50'
            }`}
          >
            <UploadCloud className={`w-8 h-8 ${dragOver ? 'text-[#635bff]' : 'text-slate-400'}`} />
            <div className="text-center">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Drop your resume here, or{' '}
                <span className="text-[#635bff]">browse</span>
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                PDF, DOC or DOCX — up to 10 MB
              </p>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPTED_TYPES.join(',')}
              onChange={onFileChange}
              className="sr-only"
            />
          </div>
        )}
        {errors.resume && <p className="mt-1 text-sm text-red-500">{errors.resume}</p>}
      </div>

      {/* Alignment */}
      <div>
        <label htmlFor="apply-alignment" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Why do you feel aligned to Adventive? <span aria-hidden="true">*</span>
        </label>
        <textarea
          id="apply-alignment"
          rows={6}
          placeholder="Tell us what draws you to Adventive and how your experience aligns with our mission…"
          value={form.alignment}
          onChange={set('alignment')}
          aria-required="true"
          aria-describedby={errors.alignment ? 'apply-alignment-error' : undefined}
          aria-invalid={!!errors.alignment}
          className={`${inputClass(!!errors.alignment)} resize-y`}
        />
        {errors.alignment && <p id="apply-alignment-error" className="mt-1 text-sm text-red-500" role="alert">{errors.alignment}</p>}
      </div>

      {/* Privacy agreement */}
      <div className="pt-2">
        <button
          type="button"
          role="checkbox"
          aria-checked={agreed}
          onClick={() => setAgreed(a => !a)}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all text-left ${
            agreed
              ? 'border-[#635bff] bg-[#635bff]/5 dark:bg-[#635bff]/10'
              : 'border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20'
          }`}
        >
          <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
            agreed ? 'bg-[#635bff] text-white' : 'border-2 border-slate-300 dark:border-slate-500'
          }`} aria-hidden="true">
            {agreed && (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <span className="text-sm text-slate-600 dark:text-slate-400">
            I agree to the{' '}
            <Link to="/legal/privacy-policy" className="text-[#635bff] underline hover:text-[#5449e6]" onClick={e => e.stopPropagation()}>
              Privacy Policy
            </Link>
            {' '}and{' '}
            <Link to="/legal/terms-conditions" className="text-[#635bff] underline hover:text-[#5449e6]" onClick={e => e.stopPropagation()}>
              Terms &amp; Conditions
            </Link>
          </span>
        </button>
      </div>

      {submitError && (
        <p className="text-sm text-red-500 text-center">{submitError}</p>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={!isReady || submitting}
        className={`w-full py-4 text-base font-semibold rounded-lg flex items-center justify-center ${
          isReady && !submitting
            ? 'stripe-button text-white'
            : 'text-slate-400 bg-slate-200 dark:bg-slate-700 dark:text-slate-500 cursor-not-allowed'
        }`}
      >
        {submitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Submitting…
          </>
        ) : (
          'Submit Application'
        )}
      </button>
    </form>
  );
};

export default ApplicationForm;
